import { getT } from "../../../locales";
import type { EditorElement } from "../../../editor/types";
import type { ElementTemplateKey } from "../types";
import {
  DEFAULT_CHOICE_APPEARANCE_EXTENDED,
  DEFAULT_IMAGE_STYLE,
  DEFAULT_AUDIO_STYLE,
  DEFAULT_VIDEO_STYLE,
  DEFAULT_MATHTYPE_STYLE,
  DEFAULT_DRAG_SOURCE_APPEARANCE,
  DEFAULT_DROP_TARGET_APPEARANCE,
  DEFAULT_ORDER_LIST_APPEARANCE,
  DEFAULT_MATCHING_POINT_APPEARANCE,
  DEFAULT_DROP_AREA,
  // DEFAULT_SUBMIT_BUTTON_STYLE,
  DEFAULT_TEXT_STYLE,
  DEFAULT_FILL_IN_THE_BLANKS_APPEARANCE,
  DEFAULT_PAINT_AREA_APPEARANCE,
  DEFAULT_PAINT_SWATCH_APPEARANCE,
  DEFAULT_PUSH_TO_TALK_APPEARANCE,
  DEFAULT_TABLE_APPEARANCE,
  // DEFAULT_TIMER_STYLE,
} from "./appearance";
import { generateElementId } from "./ids";

// Eşleştirme çift tahtası — n kaynak (sol sütun) + n hedef (sağ sütun)
// noktası, satır satır hizalı. Noktalar bağımsız (groupId yok); yazar
// her birini içeriğe (metin/resim) yaklaştırıp cevap anahtarında eşler.
function buildMatchingBoard(pairCount: number): EditorElement[] {
  const SIZE = 80;
  const GAP_Y = 100; // dikey iki satır arası
  const COL_LEFT_X = 120; // kaynak sütunu
  const COL_RIGHT_X = 320; // hedef sütunu
  const START_Y = 120;
  const elements: EditorElement[] = [];
  for (let i = 0; i < pairCount; i++) {
    const y = START_Y + i * GAP_Y;
    // Sol sütun — kaynak (sıralı sayı: 1, 2, 3…)
    elements.push({
      id: generateElementId("matching-point"),
      type: "matching-point",
      position: { x: COL_LEFT_X, y },
      size: { width: SIZE, height: SIZE },
      zIndex: 1,
      data: {
        pointType: "source",
        parentElementId: null,
        connections: [],
        appearance: {
          ...DEFAULT_MATCHING_POINT_APPEARANCE,
          borderRadius: 8, // frame modunda px (yuvarlatılmış kare)
          contentType: "text",
          contentText: `${i + 1}`,
        },
      },
    });
    // Sağ sütun — hedef (sıralı harf: A, B, C…)
    elements.push({
      id: generateElementId("matching-point"),
      type: "matching-point",
      position: { x: COL_RIGHT_X, y },
      size: { width: SIZE, height: SIZE },
      zIndex: 1,
      data: {
        pointType: "target",
        parentElementId: null,
        appearance: {
          ...DEFAULT_MATCHING_POINT_APPEARANCE,
          borderRadius: 8,
          contentType: "text",
          contentText: String.fromCharCode(64 + i + 1), // A, B, C, D…
        },
      },
    });
  }
  return elements;
}

// Bir şablon tek element ya da (eşleştirme tahtaları gibi) birden fazla
// element üretebilir.
type TemplateFactoryReturn = EditorElement | EditorElement[];

const TEMPLATE_FACTORIES: Record<
  ElementTemplateKey,
  () => TemplateFactoryReturn
> = {
  "text-basic": () => {
    const t = getT();
    return {
      id: generateElementId("text"),
      type: "text",
      position: { x: 120, y: 120 },
      size: { width: 150 },
      zIndex: 1,
      data: {
        content: t("templates.text.defaultContent"),
        style: { ...DEFAULT_TEXT_STYLE },
      },
    };
  },
  "image-basic": () => ({
    id: generateElementId("image"),
    type: "image",
    position: { x: 120, y: 120 },
    size: { width: 300, height: 200 },
    zIndex: 1,
    data: {
      style: { ...DEFAULT_IMAGE_STYLE },
    },
  }),
  // "timer-basic": () => ({
  //   id: generateElementId("timer"),
  //   type: "timer",
  //   position: { x: 120, y: 120 },
  //   size: { width: 200, height: 80 },
  //   zIndex: 1,
  //   data: {
  //     style: { ...DEFAULT_TIMER_STYLE },
  //   },
  // }),
  // "submit-button-basic": () => ({
  //   id: generateElementId("submit-button"),
  //   type: "submit-button",
  //   position: { x: 120, y: 120 },
  //   size: { width: 120, height: 48 },
  //   zIndex: 1,
  //   data: {
  //     style: { ...DEFAULT_SUBMIT_BUTTON_STYLE },
  //   },
  // }),
  "audio-basic": () => ({
    id: generateElementId("audio"),
    type: "audio",
    position: { x: 120, y: 120 },
    size: { width: 260, height: 80 },
    zIndex: 1,
    data: {
      style: { ...DEFAULT_AUDIO_STYLE },
    },
  }),
  "slide-basic": () => ({
    id: generateElementId("slide"),
    type: "slide",
    position: { x: 120, y: 120 },
    size: { width: 640, height: 360 },
    zIndex: 1,
    data: {
      style: { ...DEFAULT_VIDEO_STYLE },
    },
  }),
  "mathtype-basic": () => ({
    id: generateElementId("mathtype"),
    type: "mathtype",
    position: { x: 120, y: 120 },
    size: { width: 400, height: 80 },
    zIndex: 1,
    data: {
      style: { ...DEFAULT_MATHTYPE_STYLE },
    },
  }),
  // Legacy key for backward compatibility
  "mathlive-basic": () => ({
    id: generateElementId("mathtype"),
    type: "mathtype",
    position: { x: 120, y: 120 },
    size: { width: 400, height: 80 },
    zIndex: 1,
    data: {
      style: { ...DEFAULT_MATHTYPE_STYLE },
    },
  }),
  "choice-basic": () => {
    const t = getT();
    return {
      id: generateElementId("choice"),
      type: "choice",
      position: { x: 120, y: 120 },
      size: { width: 300, height: 290 },
      zIndex: 1,
      data: {
        options: [
          {
            id: "opt-1",
            value: "option-a",
            label: t("templates.choice.optionA"),
            contentType: "text",
          },
          {
            id: "opt-2",
            value: "option-b",
            label: t("templates.choice.optionB"),
            contentType: "text",
          },
          {
            id: "opt-3",
            value: "option-c",
            label: t("templates.choice.optionC"),
            contentType: "text",
          },
          {
            id: "opt-4",
            value: "option-d",
            label: t("templates.choice.optionD"),
            contentType: "text",
          },
        ],
        multiple: false,
        appearance: { ...DEFAULT_CHOICE_APPEARANCE_EXTENDED },
      },
    };
  },
  "drag-source-basic": () => {
    const t = getT();
    return {
      id: generateElementId("drag-source"),
      type: "drag-source",
      position: { x: 120, y: 120 },
      size: { width: 120, height: 60 },
      zIndex: 1,
      data: {
        label: t("templates.dragSource.label"),
        showLabel: false, // Show label default matches appearance setting
        appearance: { ...DEFAULT_DRAG_SOURCE_APPEARANCE },
      },
    };
  },
  "drop-target-basic": () => {
    const t = getT();
    return {
      id: generateElementId("drop-target"),
      type: "drop-target",
      position: { x: 120, y: 120 },
      size: { width: 200, height: 120 },
      zIndex: 1,
      data: {
        label: t("templates.dropTarget.label"),
        showLabel: true, // Show label by default
        acceptedSources: [], // Tüm drag source'ları kabul et (boş array = hepsini kabul et)
        appearance: { ...DEFAULT_DROP_TARGET_APPEARANCE },
        dropArea: { ...DEFAULT_DROP_AREA },
        placeholder: t("templates.dropTarget.placeholder"),
        showPlaceholder: true, // Show placeholder by default
        droppedItems: [],
      },
    };
  },
  "order-list-basic": () => {
    const t = getT();
    return {
      id: generateElementId("order-list"),
      type: "order-list",
      position: { x: 120, y: 120 },
      size: { width: 500, height: 120 },
      zIndex: 1,
      data: {
        label: t("templates.orderList.label"),
        showLabel: true, // Show label by default
        appearance: { ...DEFAULT_ORDER_LIST_APPEARANCE },
        placeholder: t("templates.orderList.placeholder"),
        showPlaceholder: true, // Show placeholder by default
        items: [
          { id: "item-1", contentType: "text", label: t("templates.orderList.item1") },
          { id: "item-2", contentType: "text", label: t("templates.orderList.item2") },
          { id: "item-3", contentType: "text", label: t("templates.orderList.item3") },
        ],
      },
    };
  },
  "matching-point-source-basic": () => ({
    id: generateElementId("matching-point"),
    type: "matching-point",
    position: { x: 120, y: 120 },
    size: { width: 20, height: 20 },
    zIndex: 1,
    data: {
      pointType: "source",
      parentElementId: null,
      connections: [],
      appearance: { ...DEFAULT_MATCHING_POINT_APPEARANCE },
    },
  }),
  "matching-point-target-basic": () => ({
    id: generateElementId("matching-point"),
    type: "matching-point",
    position: { x: 120, y: 120 },
    size: { width: 20, height: 20 },
    zIndex: 1,
    data: {
      pointType: "target",
      parentElementId: null,
      appearance: { ...DEFAULT_MATCHING_POINT_APPEARANCE },
    },
  }),
  // Eşleştirme çiftleri — hazır 2/3/4 kaynak+hedef nokta tahtası.
  "matching-2-pairs": () => buildMatchingBoard(2),
  "matching-3-pairs": () => buildMatchingBoard(3),
  "matching-4-pairs": () => buildMatchingBoard(4),
  "fill-in-the-blanks-basic": () => {
    const content = "[]";
    const matches = content.match(/\[(.*?)\]/g) || [];
    const blanks = matches.map((match) => ({
      id: `blank-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      value: match.slice(1, -1)
    }));
    
    return {
      id: generateElementId("fill-in-the-blanks"),
      type: "fill-in-the-blanks",
      position: { x: 120, y: 120 },
      size: { width: 500, height: 150 },
      zIndex: 1,
      data: {
        content,
        blanks,
        appearance: { ...DEFAULT_FILL_IN_THE_BLANKS_APPEARANCE },
      },
    };
  },
  "paint-area-basic": () => ({
    id: generateElementId("paint-area"),
    type: "paint-area",
    position: { x: 120, y: 120 },
    size: { width: 120, height: 120 },
    zIndex: 1,
    data: {
      appearance: {
        ...DEFAULT_PAINT_AREA_APPEARANCE,
        shapeType: "star",
      },
      paintedColor: undefined, // Current painted color for this area
    },
  }),
  "paint-swatch-basic": () => ({
    id: generateElementId("paint-swatch"),
    type: "paint-swatch",
    position: { x: 120, y: 120 },
    size: { width: 40, height: 40 },
    zIndex: 1,
    data: {
      appearance: { ...DEFAULT_PAINT_SWATCH_APPEARANCE },
    },
  }),
  // ─── Tıklayarak Sayma ───────────────────────────────────────
  // Hedef alanda N adet nesne. Öğrenci her birine tıkladıkça üzerine
  // 1'den başlayan numara yazılır + sesli okuma. Numara yazma, sesli
  // okuma ve numaralandırma stili Inspector'dan açılır/kapanır.
  "count-area-basic": () => ({
    id: generateElementId("count-area"),
    type: "count-area",
    position: { x: 120, y: 120 },
    size: { width: 500, height: 240 },
    zIndex: 1,
    data: {
      items: [
        {
          id: `cnt-${Date.now()}-1`,
          type: "elma",
          imageUrl: "",
          position: { x: 60, y: 80 },
          size: { width: 90, height: 90 },
        },
        {
          id: `cnt-${Date.now()}-2`,
          type: "elma",
          imageUrl: "",
          position: { x: 210, y: 80 },
          size: { width: 90, height: 90 },
        },
        {
          id: `cnt-${Date.now()}-3`,
          type: "elma",
          imageUrl: "",
          position: { x: 360, y: 80 },
          size: { width: 90, height: 90 },
        },
      ],
      appearance: {
        backgroundColor: "#f8fafc",
        borderColor: "#cbd5e1",
        borderWidth: 2,
        borderStyle: "dashed",
        borderRadius: 12,
        showFrame: true,
        showNumbers: true, // Tıklayınca üstüne numara yaz
        speakOnClick: true, // Tıklayınca sayıyı sesli okur
        numberingMode: "total", // "total" | "per-type"
        numberBadgeBg: "#1d4ed8",
        numberBadgeColor: "#ffffff",
        numberBadgeSize: 28,
        numberBadgeFontWeight: 700,
        speechLanguage: "tr-TR",
        startNumber: 1,
        incrementBy: 1,
      },
    },
  }),
  // ─── Klavye (Keypad) ─────────────────────────────────────────
  // Önizlemede öğrencinin tıklayarak sayı/işlem girebileceği sanal
  // klavye. Tuşlar son odaklanan input'a (genellikle bir boşluk doldurma
  // input'una) karakter ekler. 3 layout: "1-5", "0-9", "math".
  "keypad-1-5": () => ({
    id: generateElementId("keypad"),
    type: "keypad",
    position: { x: 120, y: 120 },
    size: { width: 280, height: 70 },
    zIndex: 1,
    data: {
      layout: "1-5",
      appearance: {
        backgroundColor: "#1e3a8a",
        borderRadius: 12,
        padding: 8,
        gap: 6,
        keyBg: "#ffffff",
        keyHoverBg: "#dbeafe",
        keyActiveBg: "#bfdbfe",
        keyColor: "#1e3a8a",
        keyBorderColor: "#cbd5e1",
        keyBorderWidth: 1,
        keyBorderRadius: 8,
        keyFontSize: 22,
        keyFontWeight: 700 as 400 | 500 | 600 | 700 | 800,
      },
    },
  }),
  "keypad-0-9": () => ({
    id: generateElementId("keypad"),
    type: "keypad",
    position: { x: 120, y: 120 },
    // 2 sıra × 6 sütun layout — yükseklik buna göre.
    size: { width: 280, height: 100 },
    zIndex: 1,
    data: {
      layout: "0-9",
      appearance: {
        backgroundColor: "#1e3a8a",
        borderRadius: 12,
        padding: 8,
        gap: 6,
        keyBg: "#ffffff",
        keyHoverBg: "#dbeafe",
        keyActiveBg: "#bfdbfe",
        keyColor: "#1e3a8a",
        keyBorderColor: "#cbd5e1",
        keyBorderWidth: 1,
        keyBorderRadius: 8,
        keyFontSize: 22,
        keyFontWeight: 700 as 400 | 500 | 600 | 700 | 800,
      },
    },
  }),
  "keypad-math": () => ({
    id: generateElementId("keypad"),
    type: "keypad",
    position: { x: 120, y: 120 },
    // 2 sıra × 8 sütun layout — yükseklik buna göre.
    size: { width: 380, height: 100 },
    zIndex: 1,
    data: {
      layout: "math",
      appearance: {
        backgroundColor: "#1e3a8a",
        borderRadius: 12,
        padding: 8,
        gap: 6,
        keyBg: "#ffffff",
        keyHoverBg: "#dbeafe",
        keyActiveBg: "#bfdbfe",
        keyColor: "#1e3a8a",
        keyBorderColor: "#cbd5e1",
        keyBorderWidth: 1,
        keyBorderRadius: 8,
        keyFontSize: 22,
        keyFontWeight: 700 as 400 | 500 | 600 | 700 | 800,
      },
    },
  }),
  // Oynat (Sesli Metin) — yuvarlatılmış kare + sağa bakan üçgen. Tıklanınca
  // data.text metnini tarayıcı TTS'i ile seslendirir. "Dinleyerek Cevaplama"
  // soru tipinin temel aracı.
  "play-button-basic": () => ({
    id: generateElementId("play-button"),
    type: "play-button",
    position: { x: 120, y: 120 },
    // ~1.5cm kare
    size: { width: 57, height: 57 },
    zIndex: 1,
    data: {
      text: "",
      language: "tr-TR",
      appearance: {
        buttonFill: "#1e3a8a",
        buttonBorder: "#1e3a8a",
        buttonBorderWidth: 2,
        buttonBorderRadius: 14,
        triangleFill: "#ffffff",
        triangleBorder: "#ffffff",
        triangleBorderWidth: 0,
      },
    },
  }),
  // ─── Lego/Unifix Küp (Toplama-Çıkarma) ───────────────────────
  // Hedef cube-bar (5/10 yuvalı yatay sıra) + sınırsız cube-source.
  // Öğrenci kaynaktan küp sürükleyip bara ekler; tıklayarak çıkarır.
  "cube-bar-5": () => ({
    id: generateElementId("cube-bar"),
    type: "cube-bar",
    position: { x: 120, y: 120 },
    // Gövde kenarı ~60. (5 + 0.2) × 60 = 312 + 24 padding = 336. Yükseklik 84.
    size: { width: 336, height: 84 },
    zIndex: 1,
    data: {
      slotCount: 5,
      value: 0,
      cubeColor: "#ec4899",
      borderColor: "#be185d",
      borderWidth: 2,
      circleFill: "#f9a8d4",
      circleBorder: "#be185d",
      circleBorderWidth: 2,
      addedCubeColor: "#60a5fa",
      addedBorderColor: "#2563eb",
      addedCircleFill: "#93c5fd",
      addedCircleBorder: "#2563eb",
      addedCircleBorderWidth: 2,
      emptySlotColor: "#ffffff",
      emptyBorderColor: "#e2e8f0",
      emptyCircleFill: "#cbd5e1",
      emptyCircleBorder: "transparent",
      emptyCircleBorderWidth: 0,
      barBackground: "#ffffff",
      barBorderColor: "#cbd5e1",
      barBorderWidth: 2,
      barBorderRadius: 10,
    },
  }),
  "cube-bar-10": () => ({
    id: generateElementId("cube-bar"),
    type: "cube-bar",
    position: { x: 120, y: 120 },
    // (10 + 0.2) × 60 = 612 + 24 = 636. Yükseklik 84.
    size: { width: 636, height: 84 },
    zIndex: 1,
    data: {
      slotCount: 10,
      value: 0,
      cubeColor: "#ec4899",
      borderColor: "#be185d",
      borderWidth: 2,
      circleFill: "#f9a8d4",
      circleBorder: "#be185d",
      circleBorderWidth: 2,
      addedCubeColor: "#60a5fa",
      addedBorderColor: "#2563eb",
      addedCircleFill: "#93c5fd",
      addedCircleBorder: "#2563eb",
      addedCircleBorderWidth: 2,
      emptySlotColor: "#ffffff",
      emptyBorderColor: "#e2e8f0",
      emptyCircleFill: "#cbd5e1",
      emptyCircleBorder: "transparent",
      emptyCircleBorderWidth: 0,
      barBackground: "#ffffff",
      barBorderColor: "#cbd5e1",
      barBorderWidth: 2,
      barBorderRadius: 10,
    },
  }),
  "cube-source-basic": () => ({
    id: generateElementId("cube-source"),
    type: "cube-source",
    position: { x: 120, y: 220 },
    // Gövde + stud (1.2 × 60 = 72 px geniş, 60 px yüksek).
    size: { width: 72, height: 60 },
    zIndex: 1,
    data: {
      cubeColor: "#60a5fa",
      borderColor: "#2563eb",
      borderWidth: 2,
      circleFill: "#93c5fd",
      circleBorder: "#2563eb",
      circleBorderWidth: 2,
      label: "Küp",
    },
  }),
  "shape-basic": () => ({
    id: generateElementId("shape"),
    type: "shape",
    position: { x: 120, y: 120 },
    size: { width: 160, height: 160 },
    zIndex: 1,
    data: {
      kind: "rectangle", // ShapeKind from lib/shapes.tsx
      fill: "#dbeafe",
      stroke: "#1d4ed8",
      strokeWidth: 2,
      strokeStyle: "solid", // solid | dashed | dotted
    },
  }),
  "color-palette-basic": () => ({
    id: generateElementId("color-palette"),
    type: "color-palette",
    position: { x: 120, y: 120 },
    size: { width: 280, height: 100 },
    zIndex: 1,
    data: {
      colors: [
        "#ef4444",
        "#f97316",
        "#f59e0b",
        "#eab308",
        "#84cc16",
        "#22c55e",
        "#06b6d4",
        "#3b82f6",
        "#8b5cf6",
        "#ec4899",
        "#000000",
        "#ffffff",
      ],
      columns: 6,
      gap: 4,
      borderRadius: 6,
      borderColor: "#e5e7eb",
      borderWidth: 1,
      showLabels: false,
    },
  }),
  "push-to-talk-basic": () => ({
    id: generateElementId("push-to-talk"),
    type: "push-to-talk",
    position: { x: 120, y: 120 },
    size: { width: 360, height: 220 },
    zIndex: 1,
    data: {
      appearance: { ...DEFAULT_PUSH_TO_TALK_APPEARANCE },
    },
  }),
  "table-basic": () => {
    // 3x3 default table oluştur
    const rows = 3;
    const columns = 3;
    const cells = [];

    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < columns; col++) {
        cells.push({
          id: `cell-${row}-${col}`,
          row,
          col,
          elements: [], // Empty array of element IDs
        });
      }
    }

    return {
      id: generateElementId("table"),
      type: "table",
      position: { x: 120, y: 120 },
      size: { width: 400, height: 300 },
      zIndex: 1,
      data: {
        rows,
        columns,
        cells,
        rowHeights: Array(rows).fill(100), // Default 100px her satır
        columnWidths: Array(columns).fill(133), // Default ~133px her kolon (400/3)
        appearance: { ...DEFAULT_TABLE_APPEARANCE },
        cellElements: {}, // Map of elementId -> EditorElement for elements inside cells
      },
    };
  },
};

// Bir şablonun ürettiği TÜM elementleri döndürür (çoğu tek, eşleştirme
// tahtaları birden fazla). Çoklu-eleman ekleme akışı bunu kullanmalı.
export const createElementsFromTemplate = (
  template: ElementTemplateKey
): EditorElement[] => {
  const result = TEMPLATE_FACTORIES[template]();
  return Array.isArray(result) ? result : [result];
};

// Geriye dönük uyumluluk: tek element bekleyen çağrılar için ilk elementi
// döndürür (örn. sürükleme önizlemesinde boyut hesabı).
export const createElementFromTemplate = (
  template: ElementTemplateKey
): EditorElement => {
  return createElementsFromTemplate(template)[0];
};
